import React, { useState } from 'react';
import { Calendar, Loader2 } from 'lucide-react';
import {
  Button,
  DatePicker,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@roger/ui-components';

interface QuickDiscontinueButtonProps {
  minDate: Date;
  onDiscontinue: (date: Date) => Promise<void>;
}

const QuickDiscontinueButton: React.FC<QuickDiscontinueButtonProps> = ({
  minDate,
  onDiscontinue,
}) => {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDiscontinue = async () => {
    if (!date) return;
    setIsSubmitting(true);
    try {
      await onDiscontinue(date);
      setOpen(false);
      setDate(undefined);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={nextOpen => {
        setOpen(nextOpen);
        if (!nextOpen) setDate(undefined);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant='ghost'
          size='sm'
          className='h-8 w-8 p-0 text-muted-foreground hover:text-foreground'
        >
          <Calendar className='h-4 w-4' />
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-auto p-3' align='start'>
        <div className='space-y-3'>
          <Label className='text-sm font-medium'>Discontinue Date</Label>
          <DatePicker
            date={date}
            onDateChange={setDate}
            placeholder='Select date'
            datePickerSize='default'
            minDate={minDate}
          />
          <div className='flex justify-end gap-2'>
            <Button
              variant='outline'
              size='sm'
              onClick={() => {
                setOpen(false);
                setDate(undefined);
              }}
            >
              Cancel
            </Button>
            <Button
              size='sm'
              onClick={handleDiscontinue}
              disabled={!date || isSubmitting}
            >
              {isSubmitting && (
                <Loader2 className='h-4 w-4 mr-1 animate-spin' />
              )}
              Discontinue
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default QuickDiscontinueButton;
